import {
  Button,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  TimePicker,
  message,
} from "antd";
import moment from "moment";
import { camelCase, flatMap, isEmpty, isNil, keyBy } from "lodash";
import { CandidateDateField } from "common/components/candidateDateField/candidateDateField";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Candidate from "redux/models/candidate";
import ClientWorkflow from "redux/models/clientWorkflow";
import Job from "redux/models/job";
import Status from "redux/models/status";
import { selectCandidates } from "redux/slices/candidateSlice";
import { selectUser } from "redux/slices/userSlice";
import K from "utilities/constants";
import { getCandidateStatusKey, isFinalStatus } from "utilities/candidate";
import { isValidMomentDate } from "utilities/dateUtility";
import {
  calculateWorkingDays,
  isPermissionPresent,
} from "utilities/generalUtility";
import { Flex } from "common/components/flex/flex";
import BillingListModal from "../../../billingListModal";
import { DisabledSubStatusAlert } from "../../../disabledSubStatusAlert/disabledSubStatusAlert";
import { GuaranteePeriodAlert } from "../../../GuaranteePeriodAlert/guaranteePeriodAlert";
import OfferedSalaryModal from "../../../offeredSalaryModal";
import LastOpeningFilledModal from "../../../lastOpeningFilledModal";
import {
  getAvailableStatusNames,
  hasTransitionToOtherStatus,
  hasTransitionToStatus,
  hasTransitionToSubStatus,
  shouldShowDisabledStatusAlert,
} from "../../../utils";
import { Section } from "../../section";
import styles from "./statusDetails.module.scss";

const { Option } = Select;

const { S2Interview } = K.Status;
const { Contacted } = S2Interview.subStatus;
const { StageStatus } = K.CandidatePipeline;

const FinalDispositionStatuses = [
  K.CandidateStatus.Declined,
  K.CandidateStatus.Hired,
  K.CandidateStatus.Terminated,
];

export const StatusDetails = ({
  clientId,
  jobDetails,
  candidateJobEntry,
  setCandidateJobEntry,
  isAssignedToJob,
  stageStatus,
  stageDateFields = [],
  onStatusUpdated,
  onStatusHistoryChange,
  onDateChanged,
  ...props
}) => {
  const [form] = Form.useForm();
  const userSlice = useSelector(selectUser);
  const candidatesSlice = useSelector(selectCandidates);

  const canChangeStatus =
    isPermissionPresent(K.Permissions.EditCandidate, userSlice.roles) &&
    isAssignedToJob;

  const [loading, setLoading] = useState(false);
  const [dateFields, setDateFields] = useState([]);
  const [dateFieldsLoading, setDateFieldsLoading] = useState(false);

  const [statusHistory, setStatusHistory] = useState({
    currentStatus: "",
    currentSubStatus: "",
    currentOtherStatus: "",
    currentStatusType: "active",
    previousStatus: "",
    previousSubStatus: "",
    previousOtherStatus: "",
    previousStatusType: "active",
  });

  const [transitionsData, setTransitionsData] = useState({
    transitions: [],
    isUnrestricted: false,
    statusKey: null,
  });

  const [listing, setListing] = useState({ status: [] });

  const [offeredSalaryModal, setOfferedSalaryModal] = useState({
    showModal: false,
    currentStatus: null,
  });

  const [guranteePeriod, setGuaranteePeriod] = useState({ workingDays: null });

  const [billingListModal, setBillingListModal] = useState({
    showModal: false,
    billingList: [],
    shouldReinitialize: false,
    isLoading: false,
  });

  const [lastOpeningFilledModal, setLastOpeningFilledModal] = useState({
    showModal: false,
    movingCandidate: false,
    shouldReinitialize: false,
    activeCandidateCount: null,
  });

  const selectedStatus = listing.status.find(
    (s) => s.name === statusHistory.currentStatus,
  );

  const availableSubStatuses = selectedStatus?.subStatuses || [];

  const selectedSubStatus = selectedStatus?.subStatuses.find(
    (st) => st.name === statusHistory.currentSubStatus,
  );

  const sameStatus = candidateJobEntry.statusId === selectedStatus?.id;
  const sameSubStatus = candidateJobEntry.subStatusId === selectedSubStatus?.id;
  const isOfferAccepted =
    statusHistory.currentSubStatus === K.Status.Offer.subStatus.Accepted.name;

  const otherStatusesToDisplay = !isEmpty(selectedStatus?.otherStatuses)
    ? selectedStatus?.otherStatuses
    : selectedSubStatus?.otherStatuses || [];

  const isStatusChanged =
    statusHistory.currentStatus !== statusHistory.previousStatus ||
    statusHistory.currentSubStatus !== statusHistory.previousSubStatus ||
    statusHistory.currentOtherStatus !== statusHistory.previousOtherStatus;

  const statusesById = useMemo(
    () => keyBy(listing.status, "id"),
    [listing.status],
  );

  const subStatusesById = useMemo(
    () => keyBy(flatMap(listing.status, "subStatuses"), "id"),
    [listing.status],
  );

  const currentStatusKey = getCandidateStatusKey(
    candidateJobEntry.statusId,
    candidateJobEntry.subStatusId,
    candidateJobEntry.otherStatusId,
  );

  const currentStatus = statusesById[candidateJobEntry.statusId];
  const currentSubStatus = subStatusesById[candidateJobEntry.subStatusId];
  const currentOtherStatus = candidateJobEntry?.otherStatus;

  const isRevertingFinalDisposition = FinalDispositionStatuses.includes(
    statusHistory.previousStatus,
  );

  const canUndoStatus =
    isPermissionPresent(K.Permissions.UndoCandidate, userSlice.roles) &&
    isAssignedToJob &&
    (!isRevertingFinalDisposition ||
      isPermissionPresent(
        K.Permissions.RevertCandidateFinalDispositionStatus,
        userSlice.roles,
      ));

  const isStatusSelected = (status) => statusHistory.currentStatus === status;

  const isSubStatusSelected = (status, subStatus) =>
    statusHistory.currentStatus === status &&
    statusHistory.currentSubStatus === subStatus;

  const fetchDateFields = useCallback(
    async (targetStatusId, targetSubStatusId, targetOtherStatusId) => {
      setDateFieldsLoading(true);

      try {
        const params = {
          targetStatusId,
          targetSubStatusId,
          targetOtherStatusId: targetOtherStatusId ?? undefined,
        };

        const { data } = await Candidate.v2.getTransitionDateFields(
          candidateJobEntry.id,
          params,
        );

        data.forEach((f) =>
          form.setFieldValue(
            camelCase(f.fieldName),
            f.isRequired ? moment() : undefined,
          ),
        );

        setDateFields(data);
      } catch {
        message.error("Failed to load date fields");
      } finally {
        setDateFieldsLoading(false);
      }
    },
    [candidateJobEntry.id, form],
  );

  const formatFormValues = (values) => {
    const timeFields = [
      "interviewTime",
      "secondInterviewTime",
      "thirdInterviewTime",
    ];
    return Object.fromEntries(
      Object.entries(values).map(([key, value]) => {
        if (isValidMomentDate(value) && !timeFields.includes(key))
          return [key, value.format(K.DateFormat.Response)];
        if (isValidMomentDate(value) && timeFields.includes(key))
          return [key, value.format(K.TimeFomat.Response)];
        return [key, value];
      }),
    );
  };

  const onStatusChange = (_, option) => {
    form.setFieldsValue({ subStatusId: null, otherStatusId: null });
    setDateFields([]);

    setStatusHistory({
      ...statusHistory,
      currentStatus: option.label,
      currentSubStatus: "",
      currentOtherStatus: "",
      currentStatusType: option.type,
    });
  };

  const getDefaultOtherStatus = (subStatus) => {
    if (
      isStatusSelected(K.CandidateStatus.S2Interview) &&
      subStatus === Contacted.name
    ) {
      return (
        candidateJobEntry?.otherStatus ?? Contacted.otherStatus.FirstContacted
      );
    }
    return null;
  };

  const onSubStatusChange = (_, option) => {
    if (
      (statusHistory.currentStatus === K.CandidateStatus.Offer &&
        option.label === "Accepted") ||
      (statusHistory.currentStatus === K.CandidateStatus.Hired &&
        option.label === "Started Work")
    ) {
      setOfferedSalaryModal((prev) => ({
        ...prev,
        showModal: true,
        currentStatus: statusHistory.currentStatus,
      }));
    }

    const defaultOtherStatus = getDefaultOtherStatus(option.label);
    form.setFieldsValue({ otherStatusId: defaultOtherStatus?.id ?? null });

    setStatusHistory({
      ...statusHistory,
      currentSubStatus: option.label,
      currentOtherStatus: defaultOtherStatus?.name ?? "",
    });

    if (option.value !== candidateJobEntry.subStatusId) {
      fetchDateFields(
        selectedStatus?.id,
        option.value,
        defaultOtherStatus?.id ?? null,
      );
    } else {
      setDateFields([]);
    }
  };

  const onOtherStatusChanged = (_, option) => {
    setStatusHistory({ ...statusHistory, currentOtherStatus: option.label });

    if (option.value !== candidateJobEntry.otherStatusId) {
      fetchDateFields(selectedStatus?.id, selectedSubStatus?.id, option.value);
    } else {
      setDateFields([]);
    }
  };

  const handleCancelStatusChange = () => {
    form.setFieldsValue({
      statusId: candidateJobEntry.statusId,
      subStatusId: candidateJobEntry.subStatusId,
      otherStatusId: candidateJobEntry.otherStatusId,
    });

    setStatusHistory((prev) => ({
      ...prev,
      currentStatus: prev.previousStatus,
      currentSubStatus: prev.previousSubStatus,
      currentOtherStatus: prev.previousOtherStatus,
      currentStatusType: prev.previousStatusType,
    }));

    setDateFields([]);
  };

  const onFinish = async (values) => {
    const payload = {
      candidateJobEntryId: candidateJobEntry.id,
      issueCreditBillingIds: billingListModal.billingList?.map(({ id }) => id),
      isCreditBilling: values.isCreditBilling,
      issueCreditReason: values.issueCreditReason,
      lastOpeningFilled: lastOpeningFilledModal.movingCandidate,
    };

    try {
      setLoading(true);
      const { updatedCandidate, declinedCandidates } =
        await Candidate.changeStatus({
          ...payload,
          ...formatFormValues(values),
        });

      setLastOpeningFilledModal((prev) => ({
        ...prev,
        showModal: false,
        movingCandidate: false,
      }));

      message.success("Candidate status changed successfully");
      setBillingListModal((prev) => ({
        ...prev,
        isLoading: true,
        showModal: false,
      }));
      form.setFieldsValue({ issueCreditReason: null, isCreditBilling: true });
      setBillingListModal((prev) => ({ ...prev, billingList: [] }));
      setDateFields([]);
      await onStatusUpdated?.(updatedCandidate, declinedCandidates);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const undoStatus = async () => {
    try {
      const res = await Status.undoCandidateStatus(candidateJobEntry.id);
      setCandidateJobEntry(res.data);
      setOfferedSalaryModal((prev) => ({
        ...prev,
        shouldReinitialize: !prev.shouldReinitialize,
      }));
      message.success(res.message);
      onStatusUpdated?.(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const setInitialValues = (statuses) => {
    const initialStatus = statuses.find(
      (status) => status.id === candidateJobEntry.statusId,
    );
    const initialSubStatus = initialStatus?.subStatuses.find(
      (ss) => ss.id === candidateJobEntry.subStatusId,
    );
    const otherStatusName = candidateJobEntry.otherStatus?.name;

    form.setFieldsValue({
      statusId: candidateJobEntry.statusId,
      subStatusId: candidateJobEntry.subStatusId,
      otherStatusId: candidateJobEntry.otherStatusId,
    });

    setStatusHistory({
      previousStatus: initialStatus?.name ?? "",
      previousSubStatus: initialSubStatus?.name ?? "",
      previousOtherStatus: otherStatusName ?? "",
      previousStatusType: initialStatus?.type ?? "active",
      currentStatus: initialStatus?.name ?? "",
      currentSubStatus: initialSubStatus?.name ?? "",
      currentOtherStatus: otherStatusName ?? "",
      currentStatusType: initialStatus?.type ?? "active",
    });
  };

  const getAllStatuses = async () => {
    try {
      const { data: statuses } = await ClientWorkflow.getStatuses(clientId);
      setListing({ status: statuses });
      setInitialValues(statuses);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCalculateWorkingDays = (date) => {
    if (isNil(date)) {
      setGuaranteePeriod((prev) => ({ ...prev, workingDays: 0 }));
      return;
    }
    const workingDays = calculateWorkingDays(
      candidateJobEntry.startDate,
      date.format("YYYY-MM-DD"),
      candidateJobEntry.isWorkingDays,
    );
    setGuaranteePeriod((prev) => ({ ...prev, workingDays }));
  };

  const onSaveClicked = async () => {
    if (statusHistory.currentStatus === K.Status.Hired.name) {
      const { data } = await Job.v2.getHiringInfo(jobDetails.id);
      const { remainOpenings, eligibleCandidateCount } = data;
      const activeCandidateCount = eligibleCandidateCount - 1;
      if (remainOpenings <= 1 && activeCandidateCount > 0) {
        setLastOpeningFilledModal((prev) => ({
          ...prev,
          showModal: true,
          activeCandidateCount,
        }));
        return;
      }
    }
    form.submit();
  };

  const getAvailableTransitions = async () => {
    try {
      const { data } = await Candidate.v2.getAvailableTransitions(
        candidateJobEntry.id,
      );
      const statusKey = getCandidateStatusKey(
        candidateJobEntry.statusId,
        candidateJobEntry.subStatusId,
        candidateJobEntry.otherStatusId,
      );
      setTransitionsData({ ...data, statusKey });
    } catch (e) {
      message.error("Failed to fetch candidate available status transitions");
      console.error(e);
    }
  };

  const isStatusItemDisabled = (status) => {
    if (transitionsData.isUnrestricted) {
      return !!currentStatus && status.orderId < currentStatus.orderId;
    }
    return (
      status.id !== currentStatus?.id &&
      !hasTransitionToStatus(status, transitionsData.transitions)
    );
  };

  const isSubStatusItemDisabled = (subStatus) => {
    if (transitionsData.isUnrestricted) {
      return (
        sameStatus &&
        !!currentSubStatus &&
        subStatus.orderId < currentSubStatus.orderId
      );
    }
    return (
      subStatus.id !== currentSubStatus?.id &&
      !hasTransitionToSubStatus(subStatus, transitionsData.transitions)
    );
  };

  const isOtherStatusItemDisabled = (otherStatus) => {
    if (transitionsData.isUnrestricted) {
      return (
        !isOfferAccepted &&
        sameStatus &&
        sameSubStatus &&
        !!currentOtherStatus &&
        otherStatus.orderId < currentOtherStatus.orderId
      );
    }
    return (
      otherStatus.id !== currentOtherStatus?.id &&
      !hasTransitionToOtherStatus(
        selectedSubStatus,
        otherStatus,
        transitionsData.transitions,
      )
    );
  };

  const renderDateField = (rule) => {
    const fieldName = camelCase(rule.fieldName);

    const isTerminatedGuaranteeDate =
      rule.fieldName === "term_date" &&
      isStatusChanged &&
      statusHistory.currentStatus === K.Status.Terminated.name &&
      statusHistory.currentSubStatus ===
        K.Status.Terminated.subStatus.WithinGuarantee.name;

    return (
      <Col key={rule.id} xs={24} sm={12}>
        <Form.Item
          name={fieldName}
          label={rule.label}
          rules={
            rule.isRequired
              ? [{ required: true, message: "This date is required" }]
              : []
          }
        >
          {rule.isTimeField ? (
            <TimePicker
              use12Hours
              format={K.TimeFomat.Display}
              placeholder={rule.label}
            />
          ) : (
            <DatePicker
              placeholder={rule.label}
              format={K.DateFormat.DashUSFormat}
              onChange={
                isTerminatedGuaranteeDate
                  ? handleCalculateWorkingDays
                  : undefined
              }
            />
          )}
        </Form.Item>
      </Col>
    );
  };

  const renderDateFieldDisplay = (rule, onUpdated, isEditable = true) => (
    <Col key={rule.id} xs={24} sm={12}>
      <CandidateDateField
        label={rule.label}
        dateKey={camelCase(rule.fieldName)}
        value={rule.value}
        candidateJobEntryId={candidateJobEntry.id}
        onUpdated={onUpdated}
        isTime={rule.isTimeField}
        isEditable={isEditable}
        disabled={!isEditable}
      />
    </Col>
  );

  const renderStatusDropdowns = () => (
    <div className={styles.statusForm__dropdowns}>
      <Form.Item
        label="Status"
        name="statusId"
        rules={[{ required: true, message: "Status is required." }]}
      >
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Status"
          onChange={onStatusChange}
          disabled={
            !canChangeStatus ||
            [
              K.CandidateStatus.Terminated,
              K.CandidateStatus.Withdrew,
              K.CandidateStatus.Declined,
            ].includes(statusHistory.previousStatus) ||
            isFinalStatus(currentSubStatus?.type) ||
            candidateJobEntry.isConsiderable === 0
          }
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
        >
          {listing.status.map((s) => (
            <Option
              disabled={isStatusItemDisabled(s)}
              key={s.id}
              value={s.id}
              type={s.type}
              label={s.name}
            >
              {s.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Sub Status"
        name="subStatusId"
        rules={[{ required: true, message: "Sub Status is required." }]}
      >
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Sub Status"
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
          disabled={
            !canChangeStatus ||
            !statusHistory.currentStatus ||
            [
              K.CandidateStatus.Terminated,
              K.CandidateStatus.Withdrew,
              K.CandidateStatus.Declined,
            ].includes(statusHistory.previousStatus) ||
            isFinalStatus(currentSubStatus?.type) ||
            candidateJobEntry.isConsiderable === 0
          }
          onChange={onSubStatusChange}
        >
          {availableSubStatuses?.map((ss) => (
            <Option
              disabled={isSubStatusItemDisabled(ss)}
              key={ss.id}
              value={ss.id}
              label={ss.name}
            >
              {ss.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Other Status"
        name="otherStatusId"
        rules={[
          {
            required: isSubStatusSelected(
              K.CandidateStatus.S2Interview,
              Contacted.name,
            ),
            message: "Other Status is required.",
          },
        ]}
      >
        <Select
          showSearch
          optionFilterProp="children"
          placeholder="Other"
          onChange={onOtherStatusChanged}
          disabled={
            !canChangeStatus ||
            !otherStatusesToDisplay.length ||
            [
              K.CandidateStatus.Terminated,
              K.CandidateStatus.Withdrew,
              K.CandidateStatus.Declined,
            ].includes(statusHistory.previousStatus) ||
            isFinalStatus(currentSubStatus?.type) ||
            (jobDetails?.isCancel ?? true) ||
            candidateJobEntry.isConsiderable === 0
          }
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
        >
          {otherStatusesToDisplay.map((st) => (
            <Option
              key={st.id}
              value={st.id}
              label={st.name}
              disabled={isOtherStatusItemDisabled(st)}
            >
              {st.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Button
        onClick={undoStatus}
        className={styles.statusForm__undoBtn}
        disabled={
          !canUndoStatus ||
          (jobDetails?.isCancel ?? true) ||
          candidateJobEntry.isConsiderable === 0
        }
      >
        <i className="icon-reload" />
      </Button>
    </div>
  );

  const renderSaveButton = () => {
    if (stageStatus !== StageStatus.Current || !isStatusChanged) {
      return null;
    }

    if (!canChangeStatus) {
      return null;
    }

    const onSave = () => {
      const isTerminatedWithinGuarantee =
        isPermissionPresent(K.Permissions.EditCandidate, userSlice.roles) &&
        statusHistory.currentStatus === K.Status.Terminated.name &&
        statusHistory.currentSubStatus ===
          K.Status.Terminated.subStatus.WithinGuarantee.name;

      if (isTerminatedWithinGuarantee) {
        setBillingListModal((prev) => ({ ...prev, showModal: true }));
        form.setFieldValue("issueCreditReason", "Terminated Within Guarantee");
        return;
      }

      onSaveClicked();
    };

    return (
      <Flex justify="flex-end" gap={16}>
        <Button type="primary" disabled={dateFieldsLoading} onClick={onSave}>
          Apply
        </Button>
        <Button onClick={handleCancelStatusChange}>Cancel</Button>
      </Flex>
    );
  };

  const onDateUpdated = (dateField) => (date, jobEntry) => {
    return onDateChanged(dateField, date, jobEntry);
  };

  const renderStageDates = () => {
    if (stageStatus === StageStatus.Current && isStatusChanged) {
      if (isEmpty(dateFields)) {
        return null;
      }

      return (
        <Row gutter={[24, 0]}>
          {dateFields.map((rule) => renderDateField(rule, selectedStatus.id))}
        </Row>
      );
    }

    return (
      <Row gutter={[24, 16]}>
        {stageDateFields.map((rule) =>
          renderDateFieldDisplay(
            rule,
            onDateUpdated(rule.fieldName),
            rule.isEditable && isAssignedToJob,
          ),
        )}
      </Row>
    );
  };

  useEffect(() => {
    onStatusHistoryChange?.(statusHistory);
  }, [statusHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    form.setFieldsValue({ statusId: undefined });
    if (!listing.status.length) getAllStatuses(clientId);
    else setInitialValues(listing.status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatesSlice.shouldReinitialize]);

  useEffect(() => {
    if (listing.status.length) setInitialValues(listing.status);
    getAvailableTransitions();
  }, [currentStatusKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Section title="Status Details">
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        className={styles.statusForm}
      >
        {stageStatus === StageStatus.Current && renderStatusDropdowns()}

        <Flex direction="column">
          {renderStageDates()}

          {!isStatusChanged &&
            stageStatus === StageStatus.Current &&
            shouldShowDisabledStatusAlert(
              currentSubStatus,
              transitionsData.statusKey,
              currentStatusKey,
            ) && (
              <DisabledSubStatusAlert
                statusName={`${currentStatus?.name} - ${currentSubStatus?.name}`}
                availableStatuses={getAvailableStatusNames(
                  transitionsData.transitions,
                  statusesById,
                  subStatusesById,
                )}
              />
            )}

          {isStatusChanged &&
            statusHistory.currentStatus === K.Status.Terminated.name &&
            statusHistory.currentSubStatus ===
              K.Status.Terminated.subStatus.WithinGuarantee.name && (
              <GuaranteePeriodAlert
                style={{ marginBottom: 16 }}
                guaranteeDays={candidateJobEntry.guaranteeDays}
                isWorkingDays={candidateJobEntry.isWorkingDays}
                workedDays={
                  guranteePeriod.workingDays ??
                  calculateWorkingDays(
                    candidateJobEntry.startDate,
                    moment().format("YYYY-MM-DD"),
                    candidateJobEntry.isWorkingDays,
                  )
                }
              />
            )}
        </Flex>

        {renderSaveButton()}

        {billingListModal.showModal && (
          <BillingListModal
            candidateActivity={candidateJobEntry}
            setBillingListModal={setBillingListModal}
            billingListModal={billingListModal}
            viewActivityForm={form}
            styles={props.styles}
            loading={loading}
          />
        )}
      </Form>
      <OfferedSalaryModal
        candidateActivity={candidateJobEntry}
        offeredSalaryModal={offeredSalaryModal}
        setCandidateActivity={setCandidateJobEntry}
        setOfferedSalaryModal={setOfferedSalaryModal}
      />
      <LastOpeningFilledModal
        isLastOpeningModalVisible={lastOpeningFilledModal}
        setIsLastOpeningModalVisible={setLastOpeningFilledModal}
        candidateActivity={candidateJobEntry}
        statusForm={form}
        loading={loading}
      />
    </Section>
  );
};
